/**
 * Magic link confirmation — the mail-scanner mitigation (SEC-2 / SEC-5).
 *
 * Better Auth verifies magic links on a GET. Corporate mail security products
 * (Defender Safe Links, Mimecast, Barracuda) pre-fetch every URL in an
 * incoming message to check it, which would consume a single-use token before
 * the recipient ever clicked. They would then see "this link has expired" — a
 * failure impossible to diagnose from a support ticket, and one that would hit
 * exactly the corporate inboxes IBBA and M&A Source staff use.
 *
 * So the emailed link lands here rather than on the verifier. This page is
 * inert on load; the token is only spent when someone presses the button.
 * Scanners issue GETs, they do not press buttons.
 */

import { T } from '../lib/copyEdit.tsx';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Wordmark from '../components/Wordmark.tsx';
import LegalFooter from '../components/LegalFooter.tsx';

export default function SignInConfirm() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<'idle' | 'verifying'>('idle');

  function confirm() {
    if (!token) return;
    setState('verifying');

    // Full navigation rather than fetch: the verifier answers with a redirect
    // and a Set-Cookie, and letting the browser follow it installs the session.
    window.location.assign(
      `/api/auth/magic-link/verify?token=${encodeURIComponent(token)}&callbackURL=${encodeURIComponent('/')}`,
    );
  }

  return (
    <div className="login-container">
      <div className="login-brand-panel">
        <div className="login-logo-img">
          <Wordmark />
        </div>
      </div>

      <div className="login-right-panel">
        <div className="login-content">
          {token ? (
            <>
              <h1 className="login-app-title"><T k="signIn.confirm.title" d="Confirm sign-in" /></h1>
              <p className="login-prompt">
                One more step, so that automated link scanners in your email cannot use this link
                before you do.
              </p>
              <hr className="login-divider" />
              <button
                type="button"
                className="login-primary-btn"
                onClick={confirm}
                disabled={state === 'verifying'}
              >
                {state === 'verifying' ? 'Signing You In…' : 'Sign Me In'}
              </button>
              <p className="login-footer-note"><T k="signIn.confirm.expiry" d="Links expire 15 minutes after they are sent." /></p>
            </>
          ) : (
            <>
              <h1 className="login-app-title"><T k="signIn.incomplete.title" d="This link is incomplete" /></h1>
              <p className="login-prompt">
                It may have been shortened or altered in transit. Request a new one and open it
                directly from your email.
              </p>
              <hr className="login-divider" />
              <a className="login-primary-btn" href="/login">
                Back to Sign In
              </a>
            </>
          )}
        </div>

        <div className="login-page-footer">
          <LegalFooter variant="page" />
        </div>
      </div>
    </div>
  );
}
